import { forwardRef, type SVGProps } from 'react'
import {
  Alert02Icon,
  AlertCircleIcon as AlertCircleIconSvg,
  BarChartHorizontalIcon as BarChartHorizontalIconSvg,
  Building06Icon,
  BulbIcon,
  ChartUpIcon,
  CheckmarkCircle01Icon,
  DashboardSquare01Icon,
  File01Icon,
  HelpCircleIcon as HelpCircleIconSvg,
  RadarIcon as RadarIconSvg,
  SortByDown01Icon,
  SecurityCheckIcon,
  SparklesIcon as SparklesIconSvg,
  TargetIcon as TargetIconSvg,
  UserIcon,
  UserSettings01Icon,
  UserMultiple02Icon,
  UserSearch01Icon,
} from '@hugeicons/core-free-icons'
import { HugeiconsIcon, type IconSvgElement } from '@hugeicons/react'
import { PROFILE_TYPE_ICON_SVGS } from './profile-type-icons'

/**
 * Creates a React component from a hugeicon definition that matches the LucideIcon API.
 * This lets consumers use `<Icon className="..." />` without knowing about HugeiconsIcon.
 */
export function createIcon(icon: IconSvgElement) {
  return forwardRef<SVGSVGElement, SVGProps<SVGSVGElement>>((props, ref) => (
    <HugeiconsIcon ref={ref} icon={icon} {...props} strokeWidth={2} />
  ))
}

/**
 * Hugeicons-backed icons that match the lucide icon API (className/size pass through), exported under the
 * same names as their lucide counterparts so call sites only swap the import source. Use these instead of
 * importing from 'lucide-react' so report section icons stay consistent with the hugeicons icon set.
 */
export const SparklesIcon = createIcon(SparklesIconSvg)
export const RadarIcon = createIcon(RadarIconSvg)
export const BarChartHorizontalIcon = createIcon(BarChartHorizontalIconSvg)
export const ArrowDownWideNarrowIcon = createIcon(SortByDown01Icon)
export const CheckCircleIcon = createIcon(CheckmarkCircle01Icon)
export const AlertTriangleIcon = createIcon(Alert02Icon)
export const AlertCircleIcon = createIcon(AlertCircleIconSvg)
export const HelpCircleIcon = createIcon(HelpCircleIconSvg)
export const TargetIcon = createIcon(TargetIconSvg)
export const LightbulbIcon = createIcon(BulbIcon)
export const TrendingUpIcon = createIcon(ChartUpIcon)

/**
 * Shared navigation and profile type icon configuration.
 * Uses hugeicons (stroke-rounded) for a clean, consistent icon style.
 * This ensures consistency across app-topbar, avatars, profile selectors, and page title sections.
 */
export const NAVIGATION_ICONS = {
  dashboard: createIcon(DashboardSquare01Icon),
  organizations: createIcon(Building06Icon),
  // The Account area (profile, billing, integrations): a person with a gear, not a bare cog.
  settings: createIcon(UserSettings01Icon),

  // Profile type icons (used in avatars and profile selectors)
  employee: createIcon(UserIcon),
  employees: createIcon(UserIcon),
  applicant: createIcon(PROFILE_TYPE_ICON_SVGS.applicant),
  applicants: createIcon(PROFILE_TYPE_ICON_SVGS.applicant),
  job: createIcon(PROFILE_TYPE_ICON_SVGS.job),
  jobs: createIcon(PROFILE_TYPE_ICON_SVGS.job),
  offer: createIcon(PROFILE_TYPE_ICON_SVGS.offer),
  offers: createIcon(PROFILE_TYPE_ICON_SVGS.offer),
  team: createIcon(PROFILE_TYPE_ICON_SVGS.team),
  teams: createIcon(PROFILE_TYPE_ICON_SVGS.team),
  teamMembers: createIcon(UserMultiple02Icon),
  orgGroup: createIcon(PROFILE_TYPE_ICON_SVGS.team),
  market: createIcon(PROFILE_TYPE_ICON_SVGS.market),
  marketOffer: createIcon(PROFILE_TYPE_ICON_SVGS.market),
  marketApplicant: createIcon(PROFILE_TYPE_ICON_SVGS.market),
  candidate: createIcon(UserSearch01Icon),
  report: createIcon(File01Icon),
  // The super-admin operations section (metrics, errors, logs, traces, sessions): a shield with a check.
  admin: createIcon(SecurityCheckIcon),
} as const

export type NavigationIcon = (typeof NAVIGATION_ICONS)[keyof typeof NAVIGATION_ICONS]
